/**
 * Author: Claude Code
 * Version: v3.2.98
 * Modified: 2025-10-13
 *
 * GameContainer - Phaser实例生命周期管理器 + 高清渲染优化
 * 负责Phaser Game实例的创建、销毁和Scene管理，不包含游戏业务逻辑
 *
 * v3.2.98更新: 清晰度适配优化
 * - pixelArt: false (启用平滑渲染，适合2D游戏)
 * - antialias: true (抗锯齿开启，文本和图形更清晰)
 * - roundPixels: false (亚像素渲染，文本边缘平滑)
 * - resolution: devicePixelRatio (HiDPI支持)
 *
 * v3.2.97更新: P0.4 Rex UI插件集成
 * - 添加phaser3-rex-plugins配置
 * - 全局Rex UI TypeScript类型声明
 * - LoadingScene支持
 */

import * as Phaser from 'phaser';
import RexUIPlugin from 'phaser3-rex-plugins/templates/ui/ui-plugin.js'; // P0.4: Rex UI plugin
import { LoadingScene } from './scenes/LoadingScene'; // P0.4: Loading scene
import { BattleScene } from './scenes/BattleScene';
import { MenuScene } from './scenes/MenuScene';
import { ReplayScene } from './scenes/ReplayScene';

// P0.4: Rex UI TypeScript declarations
declare module 'phaser' {
  interface Scene {
    rexUI: RexUIPlugin;
  }
}

export interface GameConfig {
  width: number;
  height: number;
  backgroundColor: string;
  parent: string;
  scenes: string[];
}

export interface SceneTransitionData {
  from: string;
  to: string;
  data?: any;
}

export class GameContainer {
  private static instance: GameContainer | null = null;
  private phaserGame: Phaser.Game | null = null;
  private gameConfig: GameConfig | null = null;
  private currentSceneName: string = '';
  private isInitialized: boolean = false;
  private isRunning: boolean = false;

  // Scene data bridge for cross-scene communication
  private sharedData: Map<string, any> = new Map();
  private sceneTransitionListeners: Map<string, Function[]> = new Map();

  constructor() {
    if (GameContainer.instance) {
      return GameContainer.instance;
    }
    GameContainer.instance = this;
  }

  // Singleton pattern
  static getInstance(): GameContainer {
    if (!GameContainer.instance) {
      GameContainer.instance = new GameContainer();
    }
    return GameContainer.instance;
  }

  // Initialize Phaser game instance
  initialize(config: GameConfig): void {
    if (this.isInitialized) {
      console.warn('GameContainer already initialized');
      return;
    }

    this.gameConfig = { ...config };

    // 🔥 HiDPI高清渲染配置（针对2D游戏和UI优化）
    const phaserConfig: Phaser.Types.Core.GameConfig = {
      type: Phaser.WEBGL, // 强制WebGL获得最佳性能和控制
      width: config.width,
      height: config.height,

      // 🎯 核心1: HiDPI支持 - 自动检测设备像素比（Retina屏2倍分辨率）
      resolution: window.devicePixelRatio || 1,

      // 🎯 核心2: 像素风渲染（无损缩放、最近邻采样）
      pixelArt: true,      // 使用最近邻采样，保持像素清晰
      roundPixels: true,   // 像素对齐，避免亚像素模糊
      antialias: false,    // 关闭抗锯齿（像素风）

      // 🎯 核心3: 详细渲染配置（确保所有渲染器应用设置）
      render: {
        pixelArt: true,           // 最近邻采样
        antialias: false,         // 关闭抗锯齿
        antialiasGL: false,       // 关闭WebGL抗锯齿
        roundPixels: true,        // 像素对齐
        powerPreference: 'high-performance', // 使用高性能GPU
        transparent: false,       // 不透明背景（性能更好）
        clearBeforeRender: true,  // 每帧清空画布避免残影
        premultipliedAlpha: false // 避免透明度问题
      },

      backgroundColor: config.backgroundColor,
      parent: config.parent,

      physics: {
        default: 'arcade',
        arcade: {
          gravity: { y: 0 },
          debug: false
        }
      },

      scene: this.createScenes(config.scenes),
      // 统一帧率：稳定动画播放速度（避免不同设备抖动）
      fps: {
        target: 60,
        min: 30,
        forceSetTimeOut: true, // 使用固定步长计时，保证动画速率一致
        deltaHistory: 10,
        smoothStep: true
      },

      // P0.4: Rex UI Plugin Configuration
      plugins: {
        scene: [{
          key: 'rexUI',
          plugin: RexUIPlugin,
          mapping: 'rexUI'
        }]
      },

      dom: {
        createContainer: true
      },

      // 🎯 核心4: 自适应缩放配置（响应窗口大小变化）
      scale: {
        mode: Phaser.Scale.RESIZE,        // 自动适应窗口大小
        autoCenter: Phaser.Scale.CENTER_BOTH // 居中显示
      }
    };

    try {
      this.phaserGame = new Phaser.Game(phaserConfig);
      this.isInitialized = true;
      console.log('🎮 GameContainer initialized with HiDPI rendering:', {
        resolution: phaserConfig.resolution,
        devicePixelRatio: window.devicePixelRatio,
        canvasSize: `${config.width}x${config.height}`,
        actualRenderSize: `${config.width * (phaserConfig.resolution || 1)}x${config.height * (phaserConfig.resolution || 1)}`,
        pixelArt: phaserConfig.pixelArt,
        roundPixels: (phaserConfig.render as any)?.roundPixels ?? (phaserConfig.roundPixels as any)
      });
    } catch (error) {
      console.error('Failed to initialize GameContainer:', error);
      throw error;
    }
  }

  // Create Phaser Scene instances based on config
  private createScenes(sceneNames: string[]): Phaser.Scene[] {
    const scenes: Phaser.Scene[] = [];

    sceneNames.forEach(sceneName => {
      switch (sceneName) {
        case 'LoadingScene': // P0.4: Loading scene
          scenes.push(new LoadingScene());
          break;
        case 'BattleScene':
          scenes.push(new BattleScene());
          break;
        case 'MenuScene':
          scenes.push(new MenuScene());
          break;
        case 'ReplayScene':
          scenes.push(new ReplayScene());
          break;
        default:
          console.warn(`Unknown scene: ${sceneName}`);
      }
    });

    return scenes;
  }

  // Start the game
  start(): void {
    if (!this.isInitialized) {
      throw new Error('GameContainer not initialized');
    }

    if (this.isRunning) {
      console.warn('Game is already running');
      return;
    }

    // Start with the first scene
    if (this.gameConfig && this.gameConfig.scenes.length > 0) {
      const firstScene = this.gameConfig.scenes[0];
      this.switchScene(firstScene);
      this.isRunning = true;
      console.log('Game started with scene:', firstScene);
    }
  }

  // Switch to a different scene
  switchScene(sceneName: string, data?: any): void {
    if (!this.phaserGame) {
      throw new Error('Phaser game not initialized');
    }

    const previousScene = this.currentSceneName;
    
    try {
      // Stop current scene if running
      if (this.currentSceneName) {
        const currentScene = this.phaserGame.scene.getScene(this.currentSceneName);
        if (currentScene && currentScene.scene.isActive()) {
          this.phaserGame.scene.stop(this.currentSceneName);
        }
      }

      // Start new scene：默认把 sharedData 作为启动参数传入（包含 roomId 等）
      const bootstrapData = data ?? Object.fromEntries(this.sharedData.entries());
      this.phaserGame.scene.start(sceneName, bootstrapData);

      this.currentSceneName = sceneName;

      // Notify transition listeners
      this.notifyTransitionListeners({
        from: previousScene,
        to: sceneName,
        data
      });

      console.log(`Scene switched from ${previousScene} to ${sceneName}`);
    } catch (error) {
      console.error('Failed to switch scene:', error);
      throw error;
    }
  }

  // Check if a scene exists
  hasScene(sceneName: string): boolean {
    if (!this.phaserGame) return false;
    
    return this.phaserGame.scene.getScene(sceneName) !== null;
  }

  // Get current scene name
  getCurrentScene(): string {
    return this.currentSceneName;
  }

  // Get Phaser game instance (for advanced usage)
  getGame(): Phaser.Game | null {
    return this.phaserGame;
  }

  // Get current config
  getConfig(): GameConfig | null {
    return this.gameConfig ? { ...this.gameConfig } : null;
  }

  // Check if initialized
  isGameInitialized(): boolean {
    return this.isInitialized;
  }

  // Check if running
  isGameRunning(): boolean {
    return this.isRunning;
  }

  // Scene data management
  setSceneData(key: string, value: any): void {
    this.sharedData.set(key, value);
    // 即时同步到当前活动场景的 Registry（修复 BattleScene 无法读取 roomId 的问题）
    try {
      if (key === 'roomId' && this.phaserGame) {
        const gameAny: any = this.phaserGame;
        if (gameAny?.registry?.set) { gameAny.registry.set('roomId', value); }
        if (this.currentSceneName) {
          const sceneAny: any = this.phaserGame.scene.getScene(this.currentSceneName);
          if (sceneAny?.registry?.set) { sceneAny.registry.set('roomId', value); }
        }
      }
    } catch {}
  }

  getSceneData(key: string): any {
    return this.sharedData.get(key);
  }

  clearSceneData(key?: string): void {
    if (key) {
      this.sharedData.delete(key);
    } else {
      this.sharedData.clear();
    }
  }

  // Scene transition listeners
  onSceneTransition(callback: (transition: SceneTransitionData) => void): void {
    const listeners = this.sceneTransitionListeners.get('transition') || [];
    listeners.push(callback);
    this.sceneTransitionListeners.set('transition', listeners);
  }

  private notifyTransitionListeners(transition: SceneTransitionData): void {
    const listeners = this.sceneTransitionListeners.get('transition') || [];
    listeners.forEach(callback => {
      try {
        callback(transition);
      } catch (error) {
        console.error('Scene transition listener error:', error);
      }
    });
  }

  // Pause game
  pause(): void {
    if (this.phaserGame && this.isRunning) {
      this.phaserGame.scene.pause(this.currentSceneName);
      console.log('Game paused');
    }
  }

  // Resume game
  resume(): void {
    if (this.phaserGame && this.isRunning) {
      this.phaserGame.scene.resume(this.currentSceneName);
      console.log('Game resumed');
    }
  }

  // Destroy the game instance
  destroy(): void {
    try {
      // Stop all running tweens and timers
      if (this.phaserGame) {
        // Get all running scenes and clean them up
        this.phaserGame.scene.scenes.forEach(scene => {
          if (scene.scene.isActive()) {
            // Clean up any timers
            if (scene.time) {
              scene.time.removeAllEvents();
            }
            
            // Clean up any tweens
            if (scene.tweens) {
              scene.tweens.killAll();
            }
            
            // Remove all listeners
            if (scene.events) {
              scene.events.removeAllListeners();
            }
          }
        });
        
        // Destroy the Phaser game instance
        this.phaserGame.destroy(true, false);
        this.phaserGame = null;
      }

      // Clear all data structures
      this.isInitialized = false;
      this.isRunning = false;
      this.currentSceneName = '';
      this.gameConfig = null;
      
      // Clear collections
      this.sharedData.clear();
      this.sceneTransitionListeners.clear();
      
      console.log('GameContainer destroyed successfully');
    } catch (error) {
      console.error('Error during GameContainer destruction:', error);
      
      // Force cleanup even if errors occur
      this.phaserGame = null;
      this.isInitialized = false;
      this.isRunning = false;
      this.sharedData.clear();
      this.sceneTransitionListeners.clear();
    }
  }

  // Memory cleanup for performance optimization
  cleanupMemory(): void {
    try {
      // Clear unused scene data
      const activeScene = this.currentSceneName;
      const keysToRemove: string[] = [];
      
      this.sharedData.forEach((value, key) => {
        // Remove data that doesn't belong to the current scene
        if (key.startsWith('scene_') && !key.startsWith(`scene_${activeScene}_`)) {
          keysToRemove.push(key);
        }
      });
      
      keysToRemove.forEach(key => this.sharedData.delete(key));
      
      // Force garbage collection if available (development only)
      if (typeof window !== 'undefined' && window.gc) {
        window.gc();
      }
      
      console.log(`Memory cleanup completed. Removed ${keysToRemove.length} unused entries.`);
    } catch (error) {
      console.error('Error during memory cleanup:', error);
    }
  }

  // Performance monitoring
  getPerformanceStats(): {
    initialized: boolean;
    running: boolean;
    currentScene: string;
    sharedDataSize: number;
    memoryUsage?: number;
  } {
    let memoryUsage;
    
    // Get memory usage if available (modern browsers)
    if (typeof window !== 'undefined' && (window as any).performance && (window as any).performance.memory) {
      memoryUsage = (window as any).performance.memory.usedJSHeapSize;
    }
    
    return {
      initialized: this.isInitialized,
      running: this.isRunning,
      currentScene: this.currentSceneName,
      sharedDataSize: this.sharedData.size,
      memoryUsage
    };
  }

  // Error recovery
  handleError(error: Error): void {
    console.error('GameContainer error:', error);
    
    // Try to recover by restarting current scene
    if (this.currentSceneName && this.phaserGame) {
      try {
        this.switchScene(this.currentSceneName);
        console.log('Scene restarted after error');
      } catch (recoveryError) {
        console.error('Failed to recover from error:', recoveryError);
      }
    }
  }
}

// Export singleton instance as default
export default GameContainer.getInstance();
